/* eslint-disable @typescript-eslint/naming-convention */
import { CapacitorHttp } from '@capacitor/core';

import { PermissionService } from '../services/permission.service';
import { componentsDirectory, dataDirectory, meaDefaultRegion } from '../models';

import { DataUpdateRunner } from './data-update-runner';
import { environment } from 'src/environments/environment';

type DataResource =
    | 'config'
    | 'translations'
    | 'components'
    | 'componentsFallback'
    | 'componentsConfig';

export class DataUpdater {
    permissionService: PermissionService;
    env: string;
    region: string;
    lang: string;
    bapiHeaders: Record<string, string>;

    private readonly bapiResources: DataResource[] = ['config', 'translations'];

    readonly endpointPaths: Record<DataResource, (region: string, lang: string) => string> = {
        config: () => `config/sites`,
        translations: (region: string, lang: string) =>
            `translations/${region}/${lang}`,
        components: (region: string) =>
            `${componentsDirectory}/component-data-content-${region}`,
        // Read by DataService alongside the region's component data, so they must be cached for offline use
        componentsFallback: () =>
            `${componentsDirectory}/component-data-content-${meaDefaultRegion}`,
        componentsConfig: () => `${componentsDirectory}/config`,
    };

    constructor(
        permissionService: PermissionService,
        env: string,
        userRegion: string,
        lang: string,
        bapiHeaders: Record<string, string>
    ) {
        this.permissionService = permissionService;
        this.env = env;
        this.region = userRegion;
        this.lang = lang;
        this.bapiHeaders = bapiHeaders;
    }

    async update(): Promise<void[]> {
        await this.permissionService.checkAndRequestPermission();

        const dataTypes: DataResource[] = Object.keys(
            this.endpointPaths
        ) as DataResource[];

        const promises = dataTypes.map((dataType: DataResource) =>
            this.requestData(dataType)
        );

        return Promise.all(promises);
    }

    async getDataLastModifiedDates(): Promise<(Date | null)[]> {
        await this.permissionService.checkAndRequestPermission();

        const dataTypes: DataResource[] = Object.keys(
            this.endpointPaths
        ) as DataResource[];

        const promises = dataTypes.map((dataType: DataResource) =>
            this.requestDataHeaders(dataType).then((res) => (res ? new Date(res['Last-Modified']) : null))
        );

        return Promise.all(promises);
    }

    private isBapiResource(resource: DataResource): boolean {
        return this.bapiResources.includes(resource);
    }

    /** BAPI requires an access token; without one (e.g. no session) skip it and keep the cached copy */
    private canRequest(resource: DataResource): boolean {
        if (this.isBapiResource(resource) && !this.bapiHeaders['Authorization']) {
            console.log(`Data update skipped for ${resource}: no access token`);
            return false;
        }
        return true;
    }

    private async requestData(resource: DataResource): Promise<void> {
        if (!this.canRequest(resource)) {
            return;
        }

        const fileName = this.endpointPaths[resource](this.region, this.lang);
        const filePath = this.buildFilePath(fileName);
        const url = this.buildUrl(resource, fileName);
        const headers = this.getRequestHeaders(resource);

        try {
            const result = await CapacitorHttp.get({
                url,
                responseType: 'json',
                headers,
            })
                .then((res) => {
                    // CapacitorHttp resolves on error statuses and unparseable bodies,
                    // so never cache anything but a successful JSON response
                    if (res.status < 200 || res.status >= 300 || !res.data || typeof res.data !== 'object') {
                        throw new Error(`Unexpected response (${res.status}) from ${url}`);
                    }
                    return res.data;
                })
                .catch((e) => {
                    console.log('Http data request error : ', e);
                    throw e;
                });

            return DataUpdateRunner.writeFile(
                filePath,
                JSON.stringify(result),
                true
            );
        } catch (err) {
            console.log('Data update refresh error: ', err);
        }
    }

    private requestDataHeaders(resource: DataResource): Promise<Record<string, string> | null> {
        if (!this.canRequest(resource)) {
            return Promise.resolve(null);
        }

        const fileName = this.endpointPaths[resource](this.region, this.lang);
        const url = this.buildUrl(resource, fileName);

        return CapacitorHttp.request({
            method: 'HEAD',
            url,
            headers: this.getRequestHeaders(resource),
        })
            .then((res) => res.headers)
            .catch((e) => {
                console.log('Data update headers error : ', e);
                return null;
            });
    }

    private buildUrl(resource: DataResource, fileName: string): string {
        const endpoint = this.getDataEndpoint(resource);

        if (resource === 'translations') {
            return `${endpoint}/${fileName}?amount=0`;
        }

        return this.isBapiResource(resource)
            ? `${endpoint}/${fileName}`
            : `${endpoint}/${fileName}.json`;
    }

    private getRequestHeaders(resource: DataResource): Record<string, string> {
        if (this.isBapiResource(resource)) {
            return this.bapiHeaders;
        }

        return {
            'Cache-Control':
                'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
            Expires: '0',
        };
    }

    private buildFilePath = (fileName: string, fileType: 'json' = 'json') => {
        const dataPath = dataDirectory.local;
        return `${dataPath}/${fileName}.${fileType}`;
    };

    private getDataEndpoint(resource: DataResource): string {
        if (this.isBapiResource(resource)) {
            return `${environment.apiUrl}/matrix-experience-app`;
        }

        return `${dataDirectory.external}/${environment.bapiEnv}`;
    }
}

